<template>
  <LoginSignupContainer :hide-image="isModal">
    <div class="signup-container">
      <div>
        <FriedParagraph class="branding">{{ $t('create-account') }}</FriedParagraph>
        <FriedH2 class="signup-title branding">{{ $t(title) }}</FriedH2>
      </div>
      <div class="social-signup-container">
        <FriedButton
          data-external="sign-up-with-google-button"
          class="identity-provider-btn margin-bottom"
          :button-type="ButtonType.Tertiary"
          @click="handleProviderSignup(IdentityProvider.Google)"
        >
          <template #leftIcon>
            <GoogleIcon class="icon" />
          </template>
          {{ $t('sign-up-with-google') }}
        </FriedButton>
        <FriedButton
          class="identity-provider-btn"
          :button-type="ButtonType.Tertiary"
          data-external="sign-up-with-linked-button"
          @click="handleProviderSignup(IdentityProvider.LinkedIn)"
        >
          <template #leftIcon>
            <LinkedInIcon class="icon" />
          </template>
          {{ $t('sign-up-with-linkedin') }}
        </FriedButton>
      </div>
      <FriedParagraph class="or-divider">{{ $t('or') }}</FriedParagraph>
      <FriedMessage v-if="error" class="error" :title="$t(error.title)" :type="MessageType.Error">
        <i18n-t :keypath="error.description">
          <template #contactSupportLink>
            <FriedLink
              class="contact-support"
              :normal="true"
              href="http://help.getaccept.com/"
              target="_blank"
            >
              {{ $t('contact-support') }}
            </FriedLink>
          </template>
          <template #didYouMeanLink>
            <FriedLink :normal="true" @click="handleDidYouMeanClick">
              {{ didYouMean }}
            </FriedLink>
          </template>
        </i18n-t>
      </FriedMessage>
      <form
        id="signup-form"
        ref="signupFormRef"
        data-external="signup-form"
        @submit.prevent="handleSubmit"
      >
        <FriedInput
          v-model.trim="email"
          v-autofocus
          autocomplete="user-email"
          :label="$t('work-email')"
          class="signup-input"
          :error-message="emailValidation"
          data-external="signup-email-input"
        />
        <FriedButton
          type="submit"
          :disabled="!email"
          :loading="loading"
          class="signup-button"
          :button-type="ButtonType.Branding"
          data-external="signup-submit-button"
        >
          {{ $t(buttonText) }}
        </FriedButton>
      </form>
      <FriedParagraph class="terms" :size="ParagraphSize.Small">
        <i18n-t keypath="agree-to-terms-and-conditions_v2">
          <template #termsConditionLink>
            <FriedLink
              class="bold"
              href="https://www.getaccept.com/terms-and-conditions"
              target="_blank"
            >
              {{ $t('terms-and-condition') }}
            </FriedLink>
          </template>
          <template #privacyPolicyLink>
            <FriedLink class="bold" href="https://www.getaccept.com/privacy-policy" target="_blank">
              {{ $t('privacy-policy') }}
            </FriedLink>
          </template>
        </i18n-t>
      </FriedParagraph>
      <FriedParagraph class="sign-in-link">
        {{ $t('already-have-an-account') }}
        <FriedLink class="bold" @click="redirectToLogin">{{ $t('sign-in') }}</FriedLink>
      </FriedParagraph>
    </div>
  </LoginSignupContainer>
</template>

<script lang="ts">
import { ButtonType, ParagraphSize, MessageType } from '@getaccept/fried-tofu';
import { AnalyticsEvent, AnalyticsUserTrait } from '@getaccept/lib-shared-new/src/types/analytics';
import { sanitizeEmail, validateEmail } from '@getaccept/lib-shared-new/src/helpers';
import LoginSignupContainer from '@getaccept/lib-shared-new/src/login-signup/components/LoginSignupContainer.vue';
import Analytics from '@getaccept/lib-shared-new/src/analytics';
import { Language } from '@getaccept/lib-shared-new/src/languages/enums/language';
import { RecaptchaAction } from '@getaccept/lib-shared-new/src/recaptcha/recaptcha-action';
import type { Ref } from 'vue';
import { onMounted, onUnmounted, defineComponent, computed, ref, watch } from 'vue';
import { LocalStorageHelper } from '@getaccept/lib-shared-new/src/helpers/local-storage.helper';
import { LocalStorageKey } from '@getaccept/lib-shared-new/src/enums/local-storage';
import { useI18n } from 'vue-i18n';
import GoogleIcon from '../assets/google.svg';
import LinkedInIcon from '../assets/linkedin.svg';
import { useLanguageStore } from '../../language/store/language.store';
import type { ErrorMessage } from '../../types/error-message';
import { IdentityProvider } from '../types/enums/identity-provider';
import { IdentityProviderHelper } from '../../helpers/identity-provider.helper';
import type { GeoLocation } from '../../location/types/geo-location';
import LocationService from '../../location/location.service';
import { SignupHelper } from '../../helpers/signup.helper';
import { RecaptchaService } from '../../api/recaptcha/recaptcha.service';
import { getUTMData } from '../../helpers/get-utm-data.helper';

export default defineComponent({
  name: 'Signup',
  components: {
    LoginSignupContainer,
    GoogleIcon,
    LinkedInIcon,
  },
  props: {
    templateId: { type: String, default: null },
    isModal: { type: Boolean, default: false },
    title: { type: String, default: 'welcome-to-getaccept' },
    buttonText: { type: String, default: 'sign-up' },
    registrationSource: { type: String, default: 'signup' },
    registrationCampaignId: { type: String, default: null },
    registrationEmail: { type: String, default: '' },
  },
  setup(props) {
    const { t } = useI18n();
    const email = ref('');
    const emailValidation = ref('');
    const loading = ref(false);
    const error: Ref<ErrorMessage> = ref(null);
    const signupFormRef = ref({ id: 'signup-form' });
    const languageStore = ref(useLanguageStore());

    watch(email, () => {
      emailValidation.value = '';
    });

    const didYouMean = computed(() => error.value?.variables?.didYouMean || '');

    const getEmailValidation = (email: string) => {
      if (!email) {
        return t('email-is-required').toString();
      } else if (!validateEmail(sanitizeEmail(email))) {
        return t('enter-a-valid-email').toString();
      }
      return '';
    };

    const redirectToLogin = () => {
      window.location.href = '/';
    };

    const handleDidYouMeanClick = () => {
      email.value = error.value?.variables.didYouMean;
      error.value = null;
    };

    const handleSubmit = async () => {
      error.value = null;
      const cleanEmail = sanitizeEmail(email.value);
      emailValidation.value = getEmailValidation(cleanEmail);
      const geoLocation: GeoLocation = await LocationService.getGeoLocation();
      if (!emailValidation.value) {
        const analyticsData = {
          [AnalyticsUserTrait.SignupMethod]: 'email',
          [AnalyticsUserTrait.Email]: cleanEmail,
          [AnalyticsUserTrait.SignupVersion]: 1,
          [AnalyticsUserTrait.SignupStep]: 1,
          [AnalyticsUserTrait.Country]: geoLocation.country_name,
        };
        const currentSignupData = JSON.parse(
          LocalStorageHelper.getLocalStorage(LocalStorageKey.SignupAnalyticsData)
        );
        LocalStorageHelper.setLocalStorage(
          LocalStorageKey.SignupAnalyticsData,
          JSON.stringify({
            ...currentSignupData,
            ...analyticsData,
          })
        );
        Analytics.identify(undefined, analyticsData);
        Analytics.track(AnalyticsEvent.SignupInitiated, analyticsData);
        const recaptchaToken: string = await RecaptchaService.getToken(RecaptchaAction.Signup);
        loading.value = true;
        error.value = await SignupHelper.signup({
          email: cleanEmail,
          registrationSource: props.registrationSource,
          templateId: props.templateId,
          registrationCampaign: props.registrationCampaignId,
          language: languageStore.value.language,
          recaptchaToken,
        });
        loading.value = false;
      }
    };

    const handleSignupAnalytics = () => {
      const utmData = getUTMData();
      const analyticsData = {
        [AnalyticsUserTrait.SignupVersion]: 1,
        [AnalyticsUserTrait.Browser]: navigator.userAgent,
        [AnalyticsUserTrait.SignupStep]: 0,
        ...utmData,
      };
      LocalStorageHelper.setLocalStorage(
        LocalStorageKey.SignupAnalyticsData,
        JSON.stringify(analyticsData)
      );
      Analytics.identify(undefined, analyticsData);
      Analytics.track(AnalyticsEvent.SignupLoaded, analyticsData);
    };

    const validatePostMessage = async (event: MessageEvent) => {
      const url = IdentityProviderHelper.getMessageUrl();
      if (!IdentityProviderHelper.validateMessageAndOrigin(event, url)) {
        return;
      }
      loading.value = true;
      await SignupHelper.validateIdentityProviderMessage({ event, url });
      loading.value = false;
    };

    const handleProviderSignup = async (provider: IdentityProvider) => {
      const geoLocation: GeoLocation = await LocationService.getGeoLocation();
      const analyticsData = {
        [AnalyticsUserTrait.SignupMethod]: provider,
        [AnalyticsUserTrait.SignupVersion]: 1,
        [AnalyticsUserTrait.SignupStep]: 1,
        [AnalyticsUserTrait.Country]: geoLocation.country_name,
      };
      const currentSignupData = JSON.parse(
        LocalStorageHelper.getLocalStorage(LocalStorageKey.SignupAnalyticsData)
      );
      LocalStorageHelper.setLocalStorage(
        LocalStorageKey.SignupAnalyticsData,
        JSON.stringify({
          ...currentSignupData,
          ...analyticsData,
        })
      );
      Analytics.identify(undefined, analyticsData);
      Analytics.track(AnalyticsEvent.SignupInitiated, analyticsData);
      SignupHelper.launchIdentifier({
        identityProvider: provider,
        templateId: props.templateId,
        registrationCampaignId: props.registrationCampaignId,
        language: languageStore.value.language,
      });
    };

    onMounted(() => {
      handleSignupAnalytics();
      window.addEventListener('message', validatePostMessage);
      if (languageStore.value.language !== Language.En) {
        signupFormRef.value.id = `signup-form-${languageStore.value.language}`;
      }
      if (props.registrationEmail) {
        try {
          email.value = atob(props.registrationEmail);
        } catch (e) {
          // Do nothing
        }
      }
    });

    onUnmounted(() => {
      window.removeEventListener('message', validatePostMessage);
    });

    return {
      IdentityProvider,
      ButtonType,
      ParagraphSize,
      MessageType,
      email,
      emailValidation,
      loading,
      error,
      didYouMean,
      signupFormRef,
      handleSignupAnalytics,
      redirectToLogin,
      handleDidYouMeanClick,
      handleSubmit,
      getEmailValidation,
      handleProviderSignup,
    };
  },
});
</script>

<style lang="scss" scoped>
.signup-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);
}

.social-signup-container {
  display: flex;
  flex-direction: column;

  .identity-provider-btn {
    margin: var(--spacing-25) 0;
  }
}

.signup-input {
  width: 100%;
}

.signup-button {
  margin-top: var(--spacing-100);
  width: 100%;
}

.or-divider {
  width: 100%;
  text-align: center;
  display: flex;
  gap: 1rem;
  align-items: center;
  color: var(--gray-39);

  &::after,
  &::before {
    content: '';
    flex: 1 1 auto;
    border-bottom: solid 1px var(--gray-89);
  }
}

.bold {
  font-weight: 600;
}

.error {
  margin-bottom: var(--spacing-100);

  .contact-support {
    display: contents;
  }
}

.terms,
.sign-in-link {
  color: var(--text-gray);
}

.close-icon-container {
  height: 1.12rem;
  position: absolute;
  width: 1.12rem;
  top: var(--spacing-150);
  right: var(--spacing-150);
  z-index: 2;
}
</style>
